import { apolloClient } from "api/apollo/apolloClient";
import { ListGenetecIntegrationsQuery } from "gql/apollo/graphql";
import { Integration } from "../generic/Integration";
import { CreateIntegration } from "./mutations/createGenetecIntegration";
import { DeleteIntegration } from "./mutations/deleteGenetecIntegration";
import { UpdateIntegration } from "./mutations/updateGenetecIntegration";
import { UpdateIntegrationPassword } from "./mutations/updateGenetecIntegrationPassword";
import { GetIntegration } from "./queries/getGenetecIntegration";
import { ListIntegrations } from "./queries/listGenetecIntegrations";

export const genetecIntegration: Integration = {
	type: "genetec",
	name: "Genetec",
	cardDescription: "Manage your Genetec Integrations",
	listPageDescription:
		"Add, edit, or delete your Genetec Integrations from this page",
	icon: "genetec",
	fields: [
		{
			label: "Name",
			name: "name",
			required: {
				create: true,
				update: true,
			},
			secret: false,
			type: "string",
		},
		{
			label: "Server",
			name: "server",
			required: {
				create: true,
				update: true,
			},
			secret: false,
			type: "string",
		},
		{
			label: "Username",
			name: "username",
			required: {
				create: true,
				update: true,
			},
			secret: false,
			type: "string",
		},
		{
			label: "Password",
			name: "password",
			required: {
				create: true,
				update: false,
			},
			secret: true,
			type: "password",
		},
		{
			label: "Map ID",
			name: "mapID",
			required: {
				create: false,
				update: false,
			},
			secret: false,
			type: "string",
		},
	],

	listFields: ["name", "username", "server"],

	actions: {
		get: GetIntegration,
		list: ListIntegrations,
		create: async (formData) => {
			const result = await apolloClient.mutate({
				mutation: CreateIntegration,
				variables: {
					params: {
						name: formData.name,
						server: formData.server,
						username: formData.username,
						password: formData.password,
						mapID: formData.mapID || null,
					},
				},
				update: (cache, { data: mutationResult }) => {
					if (mutationResult?.createGenetecIntegration.integration) {
						cache.updateQuery({ query: ListIntegrations }, (data) => {
							return {
								...data,
								integrations: [
									...(data?.integrations ?? []),
									mutationResult.createGenetecIntegration.integration,
								],
							} as ListGenetecIntegrationsQuery;
						});
					}
				},
			});

			return {
				error: result.data?.createGenetecIntegration.error ?? undefined,
				success: result.data?.createGenetecIntegration.success ?? undefined,
			};
		},
		update: async (gid, formData) => {
			const result = await apolloClient.mutate({
				mutation: UpdateIntegration,
				variables: {
					params: {
						gid,
						name: formData.name,
						server: formData.server,
						username: formData.username,
						mapID: formData.mapID || null,
					},
				},
				update: (cache, { data }) => {
					if (data?.updateGenetecIntegrationByGID.integration) {
						cache.modify({
							fields: {
								ListGenetecIntegrations(previous) {
									return [
										...previous,
										data.updateGenetecIntegrationByGID.integration,
									];
								},
							},
						});
					}
				},
			});

			let updatePasswordError: { message: string } | undefined;
			if (formData.password?.trim()) {
				const updatePasswordResult = await apolloClient.mutate({
					mutation: UpdateIntegrationPassword,
					variables: {
						params: {
							gid,
							password: formData.password,
						},
					},
				});

				updatePasswordError =
					updatePasswordResult.data?.updateGenetecIntegrationPasswordByGID
						.error ?? undefined;
			}

			return {
				error:
					result.data?.updateGenetecIntegrationByGID.error ??
					updatePasswordError,
				success:
					result.data?.updateGenetecIntegrationByGID.success ?? undefined,
			};
		},
		delete: async (gid) => {
			const result = await apolloClient.mutate({
				mutation: DeleteIntegration,
				variables: {
					params: {
						gid,
					},
				},
				update(cache, { data }) {
					if (data?.deleteGenetecIntegrationByGID.success) {
						const normalizedId = cache.identify({
							gid,
							__typename: "GenetecIntegration",
						});
						cache.evict({ id: normalizedId });
						cache.gc();
					}
				},
			});

			return {
				error: result.data?.deleteGenetecIntegrationByGID.error ?? undefined,
				success:
					result.data?.deleteGenetecIntegrationByGID.success ?? undefined,
			};
		},
	},

	featureFlag: "genetec",
};
